import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
  cookieOptions,
  MY_TOPICS_COOKIE_KEY,
  SearchField,
  StoryFollowChip,
  TagFollowChip,
  useFollowableStories,
  useFollowableTags,
} from '../../../shared';
import { MyTopicsFilter } from '../MyTopicsFilter';
import { MyTopicsGroup } from '../MyTopicsGroup';

import classes from './MyTopicsContent.module.scss';

interface MyTopicsContentProps {
  isModal?: boolean;
}

export const MyTopicsContent: FunctionComponent<MyTopicsContentProps> = ({ isModal }) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'myTopics' });

  const [cookies, setCookie] = useCookies([MY_TOPICS_COOKIE_KEY]);

  const [searchQuery, setSearchQuery] = useState('');

  const { feedFollowedStatus = undefined } = { ...cookies[MY_TOPICS_COOKIE_KEY] };

  const { followableStories, isFetchingFollowableStories } = useFollowableStories({
    feedFollowedStatus,
  });

  const { followableTags, isFetchingFollowableTags } = useFollowableTags({ feedFollowedStatus });

  const matchesSearchQuery = useCallback(
    (value: string) => value.toLowerCase().includes(searchQuery.toLowerCase()),
    [searchQuery]
  );

  const filterChangeHandler = useCallback(
    (feedFollowedStatus?: boolean) => {
      setCookie(MY_TOPICS_COOKIE_KEY, { feedFollowedStatus }, cookieOptions());
    },
    [setCookie]
  );

  const storyChips = useMemo(
    () =>
      followableStories
        .filter(({ title }) => matchesSearchQuery(title))
        .map((story) => <StoryFollowChip key={story.id} story={story} />),
    [followableStories, matchesSearchQuery]
  );

  const tagChips = useMemo(
    () =>
      followableTags
        .filter(({ name }) => matchesSearchQuery(name))
        .map((tag) => <TagFollowChip key={tag.id} tag={tag} />),
    [followableTags, matchesSearchQuery]
  );

  const placeholder = useMemo(() => {
    if (isFetchingFollowableStories || isFetchingFollowableTags) {
      return null;
    }

    if (storyChips.length || tagChips.length) {
      return null;
    }

    return <div className={classes['my-topics-content__empty']}>{ctxT('empty')}</div>;
  }, [ctxT, isFetchingFollowableStories, isFetchingFollowableTags, storyChips, tagChips]);

  const TitleTag = isModal ? 'h2' : 'h1';

  return (
    <div className={classes['my-topics-content']}>
      <div>
        <TitleTag className={classes['my-topics-content__title']}>{ctxT('title')}</TitleTag>
        <div className={classes['my-topics-content__sub-title']}>{ctxT('sub-title')}</div>
      </div>

      <SearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t('common.type-to-search')}
        className={classes['my-topics-content__search']}
      />

      <MyTopicsFilter feedFollowedStatus={feedFollowedStatus} onChange={filterChangeHandler} />

      <MyTopicsGroup
        title={ctxT('stories')}
        description={ctxT('stories-description')}
        chips={storyChips}
        isLoading={isFetchingFollowableStories}
        isModal={isModal}
      />

      <MyTopicsGroup
        title={ctxT('tags')}
        description={ctxT('tags-description')}
        chips={tagChips}
        isLoading={isFetchingFollowableTags}
        isModal={isModal}
      />

      {placeholder}
    </div>
  );
};
